import { Req } from '@cbo/shared-library';
import { useErrorHandler } from 'react-error-boundary';
import { Buffer } from 'buffer';
import { SingleQueryParam } from '@cbo/shared-library/request/firebase.request';
import useCallBslAndHandleErrors from '../../../lib/firebaseApiHandleErrors';
import { Paginated } from '../../../models/Paginated';
import {
  CreateWeeklySchedulePdfBodySchema,
  DeletePunchEditRequest,
  PublishScheduleRequest,
  PunchEditRequest,
  SalesSummaryData,
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
  ScheduleWeeksRangeResponse,
  WorkWeekConfig,
  ActualShiftResponseDto,
  PlannedShiftResponseDto,
  PlannedShiftReq,
  ScheduleResponse,
  CopyScheduleRequest,
  SalesSummaryDataBodySchema,
  WorkWeekConfigResponse,
  WorkWeekConfigBodySchema,
  SalesSummaryDataForWeekBodySchema,
  WorkWeeksForJurisdictionResponse,
  PublishScheduleForWeekRequest,
  CopySchedulePreviewResponse,
  EmployeeShiftOverlap,
} from '../types';
import useCurrentSite from '../../../utils/hooks/useCurrentSite';

export type SchedulingRequests = {
  getPlannedShifts: (
    page: number,
    limit: number,
    startDate: string,
    endDate: string
  ) => Promise<Paginated<PlannedShiftResponseDto> | void>;
  createShifts: (shiftInfo: PlannedShiftReq[]) => Promise<PlannedShiftResponseDto[] | void>;
  getSchedules: (calendarWeekStart: string, calendarWeekEnd: string) => Promise<ScheduleResponse[] | void>;
  getSchedulesBySelf: (calendarWeekStart: string, calendarWeekEnd: string) => Promise<ScheduleResponse[] | void>;
  updateShift: (shiftId: string, shiftInfo: PlannedShiftReq) => Promise<PlannedShiftResponseDto | void>;
  deleteShift: (shiftId: string) => Promise<void>;
  publishSchedule: (scheduleId: string, payload: PublishScheduleRequest) => Promise<ScheduleResponse | void>;
  publishScheduleForWeek: (payload: PublishScheduleForWeekRequest) => Promise<ScheduleResponse[] | void>;
  updateSchedule: (scheduleId: string, payload: ScheduleUpdateRequest) => Promise<ScheduleResponse | void>;
  createSchedule: (payload: ScheduleCreateRequest) => Promise<ScheduleResponse | void>;
  copySchedule: (payload: CopyScheduleRequest) => Promise<void>;
  getWorkWeekConfig: () => Promise<WorkWeekConfig | WorkWeekConfigResponse | void>;
  getWorkWeekConfigsForJurisdiction: (jurisdictionPath: string) => Promise<WorkWeeksForJurisdictionResponse | void>;
  getActualShifts: (
    page: number,
    limit: number,
    startDate: string,
    endDate: string,
    employeeId?: string
  ) => Promise<Paginated<ActualShiftResponseDto> | void>;
  createPunchEdit: (payload: PunchEditRequest) => Promise<ActualShiftResponseDto | void>;
  updatePunchEdit: (shiftId: string, payload: PunchEditRequest) => Promise<ActualShiftResponseDto | void>;
  deletePunchEdit: (shiftId: string, payload: DeletePunchEditRequest) => Promise<void>;
  getSalesSummary: (scheduleId: string, payload?: SalesSummaryDataBodySchema) => Promise<SalesSummaryData[] | void>;
  getSalesSummaryForWeek: (payload: SalesSummaryDataForWeekBodySchema) => Promise<SalesSummaryData[] | void>;
  generateSchedulePdf: (scheduleId: string, body: CreateWeeklySchedulePdfBodySchema) => Promise<string | undefined>;
  generateSchedulePdfByDate: (
    startDate: string,
    endDate: string,
    body: CreateWeeklySchedulePdfBodySchema
  ) => Promise<string | undefined>;
  updateWorkWeekConfig: (payload: WorkWeekConfigBodySchema) => Promise<WorkWeekConfigResponse | void>;
  getCopySchedulePreview: (
    sourceStartDate: string,
    targetStartDate: string
  ) => Promise<CopySchedulePreviewResponse | void>;
  getScheduleWeeksRange: () => Promise<ScheduleWeeksRangeResponse | void>;
  getOverlapedShiftsEmployeesIds: (
    calendarWeekStart: string,
    calendarWeekEnd: string
  ) => Promise<EmployeeShiftOverlap[] | void>;
};

const useSchedulingRequests = (): SchedulingRequests => {
  const handleError = useErrorHandler();
  const callBslAndHandleErrors = useCallBslAndHandleErrors();
  const { siteId } = useCurrentSite();

  const proxies = {
    schedules: 'schedules',
  };

  const Verb = Req.Firebase.HttpVerb;

  /**
   * SCHEDULING
   */

  const getPlannedShifts = async (
    page: number,
    limit: number,
    startDate: string,
    endDate: string
  ): Promise<Paginated<PlannedShiftResponseDto> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.schedules,
        pathSegments: ['planned-shifts'],
        enterpriseUnit: siteId,
        queryParams: [
          { kind: 'single', key: 'page', value: page.toString() },
          { kind: 'single', key: 'limit', value: limit.toString() },
          { kind: 'single', key: 'startDate', value: startDate },
          { kind: 'single', key: 'endDate', value: endDate },
        ],
      },
      handleError
    );

  const createShifts = async (shiftInfo: PlannedShiftReq[]): Promise<PlannedShiftResponseDto[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        pathSegments: ['planned-shifts', 'bulk'],
        payload: { plannedShifts: shiftInfo },
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getOverlapedShiftsEmployeesIds = async (
    calendarWeekStart: string,
    calendarWeekEnd: string
  ): Promise<EmployeeShiftOverlap[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.schedules,
        pathSegments: ['planned-shifts', 'overlaps', 'summary'],
        enterpriseUnit: siteId,
        queryParams: [
          { kind: 'single', key: 'startDate', value: calendarWeekStart },
          { kind: 'single', key: 'endDate', value: calendarWeekEnd },
        ],
      },
      handleError
    );

  const getSchedules = async (calendarWeekStart: string, calendarWeekEnd: string): Promise<ScheduleResponse[] | void> =>
    callBslAndHandleErrors(
      {
        proxy: proxies.schedules,
        pathSegments: ['schedules'],
        verb: Verb.GET,
        queryParams: [
          { kind: 'single', key: 'startDate', value: calendarWeekStart },
          { kind: 'single', key: 'endDate', value: calendarWeekEnd },
        ],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getSchedulesBySelf = async (
    calendarWeekStart: string,
    calendarWeekEnd: string
  ): Promise<ScheduleResponse[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.schedules,
        pathSegments: ['schedules', 'self'],
        queryParams: [
          { kind: 'single', key: 'startDate', value: calendarWeekStart },
          { kind: 'single', key: 'endDate', value: calendarWeekEnd },
        ],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const updateShift = async (shiftId: string, shiftInfo: PlannedShiftReq): Promise<PlannedShiftResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PATCH,
        proxy: proxies.schedules,
        pathSegments: ['planned-shifts', shiftId],
        payload: shiftInfo,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const deleteShift = async (shiftId: string): Promise<void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.DELETE,
        proxy: proxies.schedules,
        pathSegments: ['planned-shifts', shiftId],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const publishSchedule = async (
    scheduleId: string,
    payload: PublishScheduleRequest
  ): Promise<ScheduleResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        pathSegments: ['schedules', scheduleId, 'publications'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const publishScheduleForWeek = async (payload: PublishScheduleForWeekRequest): Promise<ScheduleResponse[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        pathSegments: ['schedules', 'publications'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const updateSchedule = async (scheduleId: string, payload: ScheduleUpdateRequest): Promise<ScheduleResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PATCH,
        proxy: proxies.schedules,
        pathSegments: ['schedules', scheduleId],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const createSchedule = async (payload: ScheduleCreateRequest): Promise<ScheduleResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        pathSegments: ['schedules'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getWorkWeekConfig = async (): Promise<WorkWeekConfig | WorkWeekConfigResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.schedules,
        pathSegments: ['work-week', 'config'],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getWorkWeekConfigsForJurisdiction = async (
    jurisdictionPath: string
  ): Promise<WorkWeeksForJurisdictionResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.schedules,
        pathSegments: ['jurisdictions', 'work-weeks', jurisdictionPath],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const copySchedule = async (payload: CopyScheduleRequest): Promise<void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        pathSegments: ['schedules', 'copy'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getCopySchedulePreview = async (
    sourceStartDate: string,
    targetStartDate: string
  ): Promise<CopySchedulePreviewResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.schedules,
        pathSegments: ['schedules', 'copy', 'preview'],
        queryParams: [
          { kind: 'single', key: 'sourceStartDate', value: sourceStartDate },
          { kind: 'single', key: 'targetStartDate', value: targetStartDate },
        ],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getScheduleWeeksRange = async (): Promise<ScheduleWeeksRangeResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.schedules,
        pathSegments: ['schedules', 'schedule-weeks'],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getActualShifts = async (
    page: number,
    limit: number,
    startDate: string,
    endDate: string,
    employeeId?: string
  ): Promise<Paginated<ActualShiftResponseDto> | void> => {
    if (siteId) {
      return callBslAndHandleErrors(
        {
          verb: Verb.GET,
          proxy: proxies.schedules,
          enterpriseUnit: siteId,
          pathSegments: ['actual-shifts'],
          queryParams: [
            { kind: 'single', key: 'limit', value: limit.toString() },
            { kind: 'single', key: 'page', value: page.toString() },
            { kind: 'single', key: 'startDate', value: startDate },
            { kind: 'single', key: 'endDate', value: endDate },
            ...(employeeId ? [{ kind: 'single', key: 'employeeId', value: employeeId } as SingleQueryParam] : []),
          ],
        },
        handleError
      );
    }
    return {
      items: [],
      meta: {
        itemCount: siteId.length,
        totalItems: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0,
      },
    };
  };

  const createPunchEdit = async (payload: PunchEditRequest): Promise<ActualShiftResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        pathSegments: ['punch-edit'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const updatePunchEdit = async (shiftId: string, payload: PunchEditRequest): Promise<ActualShiftResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PATCH,
        proxy: proxies.schedules,
        pathSegments: ['punch-edit', shiftId],
        enterpriseUnit: siteId,
        payload,
        queryParams: [{ kind: 'single', key: 'id', value: shiftId }],
      },
      handleError
    );

  const deletePunchEdit = async (shiftId: string, payload: DeletePunchEditRequest): Promise<void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.DELETE,
        proxy: proxies.schedules,
        pathSegments: ['punch-edit', shiftId],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getSalesSummary = async (
    scheduleId: string,
    payload?: SalesSummaryDataBodySchema
  ): Promise<SalesSummaryData[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        payload,
        pathSegments: ['schedules', scheduleId, 'sales-summary'],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getSalesSummaryForWeek = async (
    payload: SalesSummaryDataForWeekBodySchema
  ): Promise<SalesSummaryData[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        payload,
        pathSegments: ['schedules', 'sales-summary'],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const updateWorkWeekConfig = async (payload: WorkWeekConfigBodySchema): Promise<WorkWeekConfigResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        pathSegments: ['work-week'],
        payload,
      },
      handleError
    );

  const generateSchedulePdf = (
    scheduleId: string,
    body: CreateWeeklySchedulePdfBodySchema
  ): Promise<string | undefined> =>
    callBslAndHandleErrors<string, unknown>(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        payload: body,
        pathSegments: ['schedules', scheduleId, 'weekly/pdf'],
        enterpriseUnit: siteId,
      },
      handleError
    ).then((res) => {
      if (res) {
        const buffer = Buffer.from(res);
        const blob = new Blob([buffer], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
      }
      return '';
    });

  const generateSchedulePdfByDate = (
    startDate: string,
    endDate: string,
    body: CreateWeeklySchedulePdfBodySchema
  ): Promise<string | undefined> =>
    callBslAndHandleErrors<string, unknown>(
      {
        verb: Verb.POST,
        proxy: proxies.schedules,
        payload: body,
        pathSegments: ['schedules', 'weekly/pdf'],
        queryParams: [
          { kind: 'single', key: 'startDate', value: startDate },
          { kind: 'single', key: 'endDate', value: endDate },
        ],
        enterpriseUnit: siteId,
      },
      handleError
    ).then((res) => {
      if (res) {
        const buffer = Buffer.from(res);
        const blob = new Blob([buffer], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
      }
      return '';
    });

  return {
    getPlannedShifts,
    createShifts,
    getSchedules,
    getSchedulesBySelf,
    updateShift,
    deleteShift,
    publishSchedule,
    publishScheduleForWeek,
    updateSchedule,
    createSchedule,
    getWorkWeekConfig,
    getWorkWeekConfigsForJurisdiction,
    getActualShifts,
    createPunchEdit,
    updatePunchEdit,
    deletePunchEdit,
    getSalesSummary,
    getSalesSummaryForWeek,
    generateSchedulePdf,
    generateSchedulePdfByDate,
    copySchedule,
    updateWorkWeekConfig,
    getCopySchedulePreview,
    getScheduleWeeksRange,
    getOverlapedShiftsEmployeesIds,
  };
};

export default useSchedulingRequests;
